import { useCallback, useEffect, useState } from "react";
import { proxy, useSnapshot } from "valtio";
import { useEditor } from "./useEditor";
import { useMessage } from "./useMessage";
import { useAreSpreadDesignsSupported } from "./useSpreadDesigns";
import { useAreTemplatesSupported } from "./useTemplates";
import { useDesigns } from "../components/bottom-designs-menus/use-designs";
import { useAppContext } from "./useAppContext";

function getHasFillableZones(definition: eXpress.core.IDefinition | null) {
	return {
		hasFillableTextZonesInDefinition: definition
			? definition.getFillableTextZones(false).length > 0
			: undefined,
		hasFillablePhotoZonesInDefinition: definition?.hasZoneForPhotoSourceType("photo"),
	} as const;
}

function getHasFillableZonesInSpread(spread: eXpress.core.Spread | undefined) {
	return {
		hasFillableTextZonesInSpread: spread
			? spread.getFillableTextZones(false).length > 0
			: undefined,
		hasFillablePhotoZonesInSpread: spread?.hasZoneForPhotoSourceType("photo"),
		hasFillableSymbolZonesInSpread: spread
			? spread.getFillableSymbolZones(false).length > 0
			: undefined,
	} as const;
}

function getAreBackgroundColorsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.product.showBackgroundColorTab;
}

function getAreBackgroundPatternsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.product.showBackgroundLibraryTab;
}

function getAreDesignsSupported(
	definition: eXpress.core.IDefinition | null,
	designs: readonly Readonly<eXpress.IAlternativeDesign>[],
) {
	return definition?.product.showDesigns && designs?.length > 1;
}

function getAreBackgroundsSupported(definition: eXpress.core.IDefinition | null) {
	return (
		getAreBackgroundColorsSupported(definition) || getAreBackgroundPatternsSupported(definition)
	);
}

function getAreSymbolsSupported(definition: eXpress.core.IDefinition | null) {
	return definition?.editor?.isSymbolEditingEnabled();
}

function isMapSupportedOnDefinition(
	definition: eXpress.core.IDefinition | null,
	context: eXpress.api.IServicesContext,
) {
	return (
		!!context.osmTileServerUrl &&
		!!context.osmMapboxAccessToken &&
		!!definition?.hasZoneForPhotoSourceType("map")
	);
}

interface IDefinitionFeatures {
	hasFillableTextZonesInDefinition: boolean | undefined;
	hasFillablePhotoZonesInDefinition: boolean | undefined;
	areBackgroundsSupported: boolean | undefined;
	areBackgroundColorsSupported: boolean | undefined;
	areBackgroundPatternsSupported: boolean | undefined;
	areTemplatesSupported: boolean | undefined;
	areDesignsSupported: boolean | undefined;
	arePageDesignsSupported: boolean | undefined;
	areSymbolsSupported: boolean | undefined;
	isMapSupportedOnDefinition: boolean | undefined;
}

const definitionFeaturesState = proxy<IDefinitionFeatures>({
	hasFillableTextZonesInDefinition: undefined,
	hasFillablePhotoZonesInDefinition: undefined,
	areBackgroundsSupported: undefined,
	areBackgroundColorsSupported: undefined,
	areBackgroundPatternsSupported: undefined,
	areTemplatesSupported: undefined,
	areDesignsSupported: undefined,
	arePageDesignsSupported: undefined,
	areSymbolsSupported: undefined,
	isMapSupportedOnDefinition: undefined,
});

/**
 * Detect and update features that are enabled for this Definition
 * Use this once to keep state up-to-date
 */
export function useSetDefinitionFeatures() {
	const editor = useEditor();
	const { context } = useAppContext();
	const { data: designs = [] } = useDesigns();

	// Get Template informa0tion
	definitionFeaturesState.areTemplatesSupported = useAreTemplatesSupported();
	definitionFeaturesState.arePageDesignsSupported = useAreSpreadDesignsSupported();

	// Initialize
	useEffect(() => {
		const { hasFillablePhotoZonesInDefinition, hasFillableTextZonesInDefinition } =
			getHasFillableZones(editor.definition);
		definitionFeaturesState.hasFillablePhotoZonesInDefinition =
			hasFillablePhotoZonesInDefinition;
		definitionFeaturesState.hasFillableTextZonesInDefinition = hasFillableTextZonesInDefinition;
		definitionFeaturesState.areBackgroundsSupported = getAreBackgroundsSupported(
			editor.definition,
		);
		definitionFeaturesState.areBackgroundColorsSupported = getAreBackgroundColorsSupported(
			editor.definition,
		);
		definitionFeaturesState.areBackgroundPatternsSupported = getAreBackgroundPatternsSupported(
			editor.definition,
		);
		definitionFeaturesState.areDesignsSupported = getAreDesignsSupported(
			editor.definition,
			designs,
		);
		definitionFeaturesState.areSymbolsSupported =
			getAreSymbolsSupported(editor.definition) ?? false;
		definitionFeaturesState.isMapSupportedOnDefinition = isMapSupportedOnDefinition(
			editor.definition,
			context,
		);
	}, [editor, designs, context]);

	useMessage(
		useCallback(
			(message) => {
				const canZoneBeAddedOrRemoved = eXpress.core.canZoneBeAddedOrRemoved(message); // example: Apply templates

				// Can fillable zones in definition change
				if (canZoneBeAddedOrRemoved) {
					const { hasFillablePhotoZonesInDefinition, hasFillableTextZonesInDefinition } =
						getHasFillableZones(editor.definition);
					definitionFeaturesState.hasFillablePhotoZonesInDefinition =
						hasFillablePhotoZonesInDefinition;
					definitionFeaturesState.hasFillableTextZonesInDefinition =
						hasFillableTextZonesInDefinition;
				}
				definitionFeaturesState.areDesignsSupported = getAreDesignsSupported(
					editor.definition,
					designs,
				);
				definitionFeaturesState.areBackgroundsSupported = getAreBackgroundsSupported(
					editor.definition,
				);
				definitionFeaturesState.areBackgroundColorsSupported =
					getAreBackgroundColorsSupported(editor.definition);
				definitionFeaturesState.areBackgroundPatternsSupported =
					getAreBackgroundPatternsSupported(editor.definition);
				definitionFeaturesState.areSymbolsSupported =
					getAreSymbolsSupported(editor.definition) ?? false;
				definitionFeaturesState.isMapSupportedOnDefinition = isMapSupportedOnDefinition(
					editor.definition,
					context,
				);
			},
			[editor, designs, context],
		),
	);

	// Didn't return a value, this to reduce the chance of using it in multiple components
}

/**
 * Use to read the ser
 * Use this to read the values set by useSetDefinitionFeatures
 */
export function useDefinitionFeatures() {
	return useSnapshot(definitionFeaturesState);
}

/** Check which features are supported on the selected spread */
export function useSpreadFeatures() {
	const { selection } = useEditor();
	const [hasFillableZonesInSpread, setHasFillableZonesInSpread] = useState(() =>
		getHasFillableZonesInSpread(selection.getCurrentSpread()),
	);

	useMessage(
		useCallback(
			(message) => {
				const canZoneBeAddedOrRemoved = eXpress.core.canZoneBeAddedOrRemoved(message); // example: Apply templates
				const isSelectedSpreadChanged = eXpress.core.isSelectedSpreadChanged(message);

				// Can fillable Zones in spread change
				if (canZoneBeAddedOrRemoved || isSelectedSpreadChanged) {
					const spread = selection.getCurrentSpread();
					setHasFillableZonesInSpread(getHasFillableZonesInSpread(spread));
				}
			},
			[selection],
		),
	);

	return hasFillableZonesInSpread;
}
